import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Coffee, Star, TrendingUp, Search } from "lucide-react";
import { apiRaw } from "../lib/api";
import { getPhone, setPhone as saveSharedPhone, getName as savedName, setName as saveSharedName } from "../lib/session";
import LoyaltyDots from "../components/LoyaltyDots";
import MeStatsHero from "../components/MeStatsHero";

const slugify = (s) =>
    (s || "").toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 30);

const MyCafes = () => {
    const nav = useNavigate();
    const [phone, setPhoneState] = useState(getPhone());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [entered, setEntered] = useState(!!getPhone());
    const [cafeSearch, setCafeSearch] = useState("");
    const [searchStatus, setSearchStatus] = useState({ checked: false });

    const digits = phone.replace(/\D/g, "");
    const valid = digits.length >= 8 && digits.length <= 15;

    const load = async (d) => {
        setLoading(true);
        try {
            const r = await apiRaw.get(`/me/${d}`);
            setData(r.data);
            if (r.data?.name && !savedName()) saveSharedName(r.data.name);
        } catch {
            setData({ cafes: [], name: "", phone_last4: d.slice(-4) });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (entered && digits && valid) load(digits);
    }, [entered, digits, valid]);

    // Debounced slug availability lookup for "find your café by name"
    useEffect(() => {
        const s = slugify(cafeSearch);
        if (!s || s.length < 3) {
            setSearchStatus({ checked: false });
            return;
        }
        setSearchStatus({ checking: true, slug: s });
        const t = setTimeout(() => {
            apiRaw
                .get(`/signup/check-slug/${s}`)
                .then((r) => setSearchStatus({ checked: true, slug: s, exists: r.data.available === false && r.data.reason === "Already taken" }))
                .catch(() => setSearchStatus({ checked: true, slug: s, exists: false }));
        }, 350);
        return () => clearTimeout(t);
    }, [cafeSearch]);

    const submit = () => {
        if (!valid) return;
        saveSharedPhone(digits);
        setEntered(true);
    };

    return (
        <div className="min-h-screen bg-[#F9F6F0] text-[#2C1A12]">
            <header className="border-b border-[#E8DCCB]/60">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                    <button
                        data-testid="me-back-btn"
                        onClick={() => nav("/")}
                        className="flex items-center gap-2 text-sm font-semibold text-[#8C7A72] hover:text-[#2C1A12]"
                    >
                        <ArrowLeft className="h-4 w-4" strokeWidth={2.5} /> Back
                    </button>
                    <div className="font-display font-black text-xl">
                        Coffee<span className="text-[#FF4F18]">in7.</span>
                    </div>
                    <div className="w-16" />
                </div>
            </header>

            <div className="max-w-3xl mx-auto px-6 py-10">
                {!entered && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                            My cafés
                        </div>
                        <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl leading-tight">
                            Your coffee, everywhere.
                        </h1>
                        <p className="mt-3 text-[#8C7A72] max-w-lg">
                            One phone number. Every café you've been to on Coffeein7. All your
                            loyalty stamps, in one place.
                        </p>

                        <div className="mt-10 max-w-md">
                            <label className="text-xs uppercase tracking-[0.25em] font-bold text-[#8C7A72]">
                                Your mobile number
                            </label>
                            <input
                                data-testid="me-phone-input"
                                type="tel"
                                inputMode="numeric"
                                placeholder="0400 000 000"
                                value={phone}
                                onChange={(e) => setPhoneState(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && submit()}
                                className="mt-3 w-full text-4xl font-display font-black text-[#2C1A12] placeholder:text-[#E8DCCB] bg-transparent border-b-4 border-[#E8DCCB] focus:border-[#FF4F18] outline-none py-3 transition-colors"
                            />
                            <button
                                data-testid="me-continue-btn"
                                onClick={submit}
                                disabled={!valid}
                                className={`mt-8 w-full py-4 rounded-full font-display font-bold flex items-center justify-center gap-2 ${
                                    valid
                                        ? "bg-[#FF4F18] text-white active:scale-95 shadow-[0_12px_32px_rgba(255,79,24,0.32)]"
                                        : "bg-[#E8DCCB] text-[#8C7A72] cursor-not-allowed"
                                }`}
                            >
                                Show me my cafés <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                            </button>
                        </div>
                    </motion.div>
                )}

                {entered && loading && (
                    <div className="text-center py-24 text-[#8C7A72]">Loading your cafés…</div>
                )}

                {entered && !loading && data && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                            ···{data.phone_last4}
                        </div>
                        <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl leading-tight">
                            {data.name ? `Hi ${data.name.split(" ")[0]},` : "Welcome"}
                        </h1>
                        <p className="mt-3 text-[#8C7A72]">
                            {data.cafes.length > 0
                                ? `You're a regular at ${data.cafes.length} café${data.cafes.length > 1 ? "s" : ""}.`
                                : "You haven't ordered from any café on Coffeein7 yet."}
                        </p>

                        <button
                            data-testid="me-change-phone"
                            onClick={() => {
                                setPhoneState("");
                                setEntered(false);
                                setData(null);
                            }}
                            className="mt-3 text-xs text-[#8C7A72] underline"
                        >
                            Not you? Change number
                        </button>

                        <MeStatsHero
                            totals={data.totals}
                            cafeCount={data.cafes.length}
                            name={data.name}
                        />

                        {data.cafes.length === 0 ? (
                            <div className="mt-10 rounded-3xl bg-white border border-[#E8DCCB] p-8 text-center">
                                <Coffee className="h-8 w-8 text-[#FF4F18] mx-auto" strokeWidth={2} />
                                <div className="mt-4 font-display font-bold text-lg">No cafés yet</div>
                                <p className="mt-1 text-sm text-[#8C7A72]">
                                    Scan a Coffeein7 QR at a café — or type your café's name below to jump in.
                                </p>
                            </div>
                        ) : null}

                        {/* Café name lookup — shown always for first-time OR returning users who want to add a new café */}
                        <div className="mt-8 rounded-3xl bg-white border border-[#E8DCCB] p-5">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-[#8C7A72]">
                                <Search className="h-3 w-3" strokeWidth={2.5} />
                                Know your café's name?
                            </div>
                            <div className="mt-3 rounded-2xl bg-[#F9F6F0] border-2 border-[#E8DCCB] focus-within:border-[#FF4F18] transition-colors overflow-hidden flex">
                                <div className="px-3 py-3 text-[#8C7A72] text-sm font-mono border-r border-[#E8DCCB]">
                                    /
                                </div>
                                <input
                                    data-testid="me-cafe-search"
                                    type="text"
                                    value={cafeSearch}
                                    onChange={(e) => setCafeSearch(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && searchStatus.exists) {
                                            nav(`/${searchStatus.slug}`);
                                        }
                                    }}
                                    placeholder="e.g. bluebottle, brewbar, southcross"
                                    className="flex-1 px-3 py-3 bg-transparent font-mono text-sm outline-none"
                                />
                            </div>
                            <div className="mt-2 flex items-center justify-between min-h-[24px]">
                                <span className="text-xs">
                                    {searchStatus.checking && (
                                        <span className="text-[#8C7A72]">Checking…</span>
                                    )}
                                    {searchStatus.checked && searchStatus.exists && (
                                        <span className="text-[#1B995E] font-bold">Found · {searchStatus.slug}</span>
                                    )}
                                    {searchStatus.checked && !searchStatus.exists && (
                                        <span className="text-[#8C7A72]">No café at that name</span>
                                    )}
                                </span>
                                {searchStatus.exists && (
                                    <button
                                        data-testid="me-cafe-goto"
                                        onClick={() => nav(`/${searchStatus.slug}`)}
                                        className="rounded-full bg-[#FF4F18] text-white px-4 py-1.5 text-xs font-display font-bold active:scale-95 flex items-center gap-1"
                                    >
                                        Open <ArrowRight className="h-3 w-3" strokeWidth={2.5} />
                                    </button>
                                )}
                            </div>
                            <p className="mt-2 text-[10px] text-[#8C7A72]">
                                Ask the counter for the café's Coffeein7 link — it's the last part of the URL.
                            </p>
                        </div>

                        {data.cafes.length > 0 && (
                            <div className="mt-8 grid gap-4">
                                {data.cafes.map((c) => (
                                    <CafeCard key={c.tenant.slug} c={c} onOpen={() => nav(`/${c.tenant.slug}`)} />
                                ))}
                            </div>
                        )}
                    </motion.div>
                )}
            </div>
        </div>
    );
};

const CafeCard = ({ c, onOpen }) => {
    const lastDate = c.last_order_at ? new Date(c.last_order_at).toLocaleDateString([], { day: "numeric", month: "short" }) : null;
    const toNext = Math.max(0, 10 - c.stamps);
    return (
        <div
            data-testid={`me-cafe-${c.tenant.slug}`}
            onClick={onOpen}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen()}
            role="button"
            tabIndex={0}
            className="text-left rounded-3xl bg-white border border-[#E8DCCB] p-5 hover:border-[#FF4F18] hover:shadow-[0_16px_40px_rgba(255,79,24,0.08)] transition-all active:scale-[0.99] cursor-pointer focus:outline-none focus:border-[#FF4F18]"
        >
            <div className="flex items-start justify-between">
                <div>
                    <div className="font-display font-black text-2xl leading-tight">{c.tenant.name}</div>
                    <div className="mt-1 text-xs text-[#8C7A72] flex items-center gap-2">
                        {lastDate && <span>Last visit · {lastDate}</span>}
                        {c.orders_count > 0 && (
                            <>
                                <span>·</span>
                                <span>{c.orders_count} order{c.orders_count > 1 ? "s" : ""}</span>
                            </>
                        )}
                    </div>
                </div>
                <ArrowRight className="h-5 w-5 text-[#8C7A72]" strokeWidth={2.5} />
            </div>

            <div className="mt-4 rounded-2xl bg-[#F9F6F0] border border-[#E8DCCB]/60 p-3 flex items-center justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#8C7A72] font-bold flex items-center gap-1">
                        <Star className="h-3 w-3 fill-[#FF4F18] text-[#FF4F18]" strokeWidth={2} />
                        Loyalty
                    </div>
                    {c.free_coffee_available ? (
                        <div className="mt-1 font-display font-bold text-[#FF4F18] text-base">
                            Free coffee ready
                        </div>
                    ) : (
                        <div className="mt-1 font-display font-bold text-[#2C1A12] text-base">
                            {toNext} away from free
                        </div>
                    )}
                </div>
                <LoyaltyDots stamps={c.stamps} />
            </div>

            {typeof c.beans_balance === "number" && c.beans_balance > 0 && (
                <div
                    className="mt-3 rounded-2xl bg-[#2C1A12] text-white p-3 flex items-center justify-between"
                    data-testid={`me-beans-${c.tenant.slug}`}
                >
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 font-bold">
                            🫘 Beans balance
                        </div>
                        <div className="mt-0.5 font-display font-black text-lg">
                            {c.beans_balance.toLocaleString()} <span className="text-white/60 text-xs">Beans</span>
                        </div>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF4F18] font-bold">
                        Spend at rewards
                    </div>
                </div>
            )}

            {c.total_spent > 0 && (
                <div className="mt-3 flex items-center gap-2 text-xs text-[#8C7A72]">
                    <TrendingUp className="h-3.5 w-3.5" strokeWidth={2.5} />
                    Lifetime spend · ${c.total_spent.toFixed(2)}
                </div>
            )}

            <div className="mt-4 pt-4 border-t border-[#E8DCCB]/60 flex items-center justify-between gap-3">
                <button
                    data-testid={`me-wallet-${c.tenant.slug}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        window.location.assign(`/wallet`);
                    }}
                    className="text-xs font-display font-bold text-[#2C1A12] hover:text-[#FF4F18] transition-colors flex items-center gap-1.5"
                >
                    💳 Show membership card
                </button>
                <button
                    data-testid={`me-order-${c.tenant.slug}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        window.location.assign(`/${c.tenant.slug}/menu`);
                    }}
                    className="text-xs font-display font-bold text-[#FF4F18] hover:underline"
                >
                    Order →
                </button>
            </div>
        </div>
    );
};

export default MyCafes;
